// filepath: /Users/mk/work/2024/jimon-generator-web/src/components/AuthForm.tsx
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { login } from '../utils/api';
import { useAuth } from '../utils/auth';

interface AuthFormProps {
  onLogin?: (email: string) => void; // オプショナルに変更
}

const AuthForm: React.FC<AuthFormProps> = ({ onLogin }) => {
  const [isRegistering, setIsRegistering] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { checkAuth } = useAuth();
  const navigate = useNavigate();

  // サインアップ機能が有効かどうかを環境変数から取得
  const isSignupEnabled = process.env.REACT_APP_ENABLE_SIGNUP === 'true';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isLoading) return;
    setIsLoading(true);
    setError('');

    try {
      if (isRegistering) {
        // 登録処理は既存のままで
        console.log(`アカウント登録を開始します...`);
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/auth/register`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            credentials: 'include',
            body: JSON.stringify({
              email,
              password,
            }),
          }
        );

        const data = await response.json();
        if (response.ok) {
          // 登録成功時の処理
          setIsRegistering(false);
          setEmail('');
          setPassword('');
          setError('登録が完了しました。ログインしてください。');
        } else {
          throw new Error(data.message || 'エラーが発生しました');
        }
      } else {
        // ログイン処理を新しいAPI関数で行う
        console.log(`ログイン処理を開始します...`);
        await login(email, password);

        // 認証状態を更新
        await checkAuth();

        // ログイン成功の処理
        if (onLogin) {
          onLogin(email);
        }

        // ホームページへリダイレクト
        navigate('/');
      }
    } catch (error) {
      console.error('Error:', error);
      setError(
        error instanceof Error
          ? error.message
          : 'サーバーとの通信に失敗しました'
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="bg-white p-8 rounded-lg shadow-md">
      <h2 className="text-2xl font-bold mb-6 text-center">
        {isRegistering ? 'アカウント登録' : 'ログイン'}
      </h2>
      <form onSubmit={handleSubmit}>
        <div className="mb-4">
          <label
            htmlFor="email"
            className="block text-gray-700 text-sm font-bold mb-2"
          >
            メールアドレス
          </label>
          <input
            type="email"
            id="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
            required
          />
        </div>
        <div className="mb-6">
          <label
            htmlFor="password"
            className="block text-gray-700 text-sm font-bold mb-2"
          >
            パスワード
          </label>
          <input
            type="password"
            id="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
            required
          />
        </div>
        <div className="flex items-center justify-between flex-wrap">
          <button
            type="submit"
            className={`bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline ${
              isLoading ? 'opacity-50 cursor-not-allowed' : ''
            }`}
            disabled={isLoading}
          >
            {isLoading ? '処理中...' : isRegistering ? '登録する' : 'ログイン'}
          </button>
          {/* サインアップボタンは条件付きで表示 */}
          {isSignupEnabled && (
            <button
              type="button"
              onClick={() => {
                setIsRegistering(!isRegistering);
                setError('');
              }}
              className="text-blue-500 hover:text-blue-700 text-sm mt-2 md:mt-0"
            >
              {isRegistering ? 'ログイン画面に戻る' : 'アカウント登録はこちら'}
            </button>
          )}
        </div>
        {error && <p className="mt-4 text-red-500 text-xs italic">{error}</p>}
      </form>
    </div>
  );
};

export default AuthForm;
